/// <amd-module name="Dashboards/DashboardView/src/model/DashboardViewState" />
import { DashboardWidgetProps } from "Dashboards/WidgetContracts/Internal/DashboardWidgetProps";
import { ITeamData } from "Tfs/Platform/Page";
import { IIdentity } from "VSS/Features/Identities/IdentityService";
import { Project, TeamProjectItem } from "Dashboards/DashboardPanel/Common";
import { WriteableDashboard } from "./Dashboard";
import { DeepReadonly } from "./DeepReadonly";

/** Defines the member property "namespace" of the redux store state within which the dashboard view state is stored. */
export interface DashboardViewAwareState {
    readonly dashboardViewState: DashboardViewState;
}

/** State specific to the dashboard view. */
export interface DashboardViewState extends DeepReadonly<WriteableDashboardViewState> {}

/** Should only be consumed by 'produce' in the reducer as the singular place state can be modified. */
export interface WriteableDashboardViewState extends TelemetryState {
    teamData: ITeamData | undefined;

    ownerIdentity: IIdentity | undefined;

    /** Data for the project's default team, to be used by widgets on team-agnostic dashboards */
    defaultProjectTeamData: ITeamData | undefined;

    permissionsState: DashboardPermissionsState;

    /** The dashboard should render as full-screen. */
    showFullScreen: boolean;

    /** Indicates if the dashboard is rendering in an embedded scenario. */
    embedded: boolean;

    renderTimestamp: number;
    currentTimestamp: number;
    showRefreshTime: boolean;

    isThrottlingWarningActive: boolean;
    isThrottlingAllowed: boolean;

    /** Indicates whether the feature flag for not rendering widgets that are only 10% visible is enabled */
    omitTenPercentVisibleWidgetsFromRender: boolean;

    dashboard: WriteableDashboard | undefined;

    pastWidgets: DashboardWidgetProps[];

    /** The currently rendered state of widgets on the dashboard */
    widgets: DashboardWidgetProps[];

    /** The dashboard should render the template page */
    showDashboardTemplatePage: boolean;

    /** Widgets under preview. Only pertinent during config preview mode. **/
    previewWidgets: DashboardWidgetProps[] | undefined;

    /** The dashboard should render in edit mode. */
    showEditMode: boolean;

    /**  When active, the dashboard should render in widget-configuration mode.
     *  This stacks with elevated precedence over edit mode(and can operate without edit mode being active). */
    showConfigMode: boolean;

    /** The widget under configuration preview. */
    previewWidgetId: string | undefined;
    highlightOnPreview: boolean;

    /** The dashboard should render the settings manager experience. */
    showDashboardSettingsManager: boolean;

    /** The dashboard should render the copy dashboard experience */
    showCopyDashboardPanel: boolean;

    /** The dashboard component has mounted. */
    dashboardIsInitialized: boolean;

    /**
     * The IDs of widgets that have been visible within the viewport.
     * Does not mean they are currently visible in the viewport.
     */
    widgetsSeen: string[];

    /** The IDs of widgets that were visible in the viewport when the dashboard first rendered. */
    widgetsSeenInitially: string[];

    /** The IDs of widgets that have indicated they are loaded. */
    widgetsLoaded: string[];

    /**
     * Contains the latest notified handled error
     * or undefined if no error has been notified, user dismisses the error in the UI,
     * or other page events like dashboard refresh clear the error state.
     */
    errorState: ErrorState | undefined;

    /** If widget is being dragged onto the dashboard, this will contain the dragging event */
    dragEvent?: Event;

    /** ID of the widget that is being dragged */
    draggedWidgetId?: string;

    /**
     * Signals that a widget has been moved on behalf of the user automagically from position (0, 0).
     * This happens the first time a dahsboard is visited after a widget has been pinned.
     */
    unpositionedWidgetWasPlaced?: boolean;

    copyDashboardPanelState: CopyDashboardState;
}

export interface DashboardPermissionsState {
    /** Whether or not the user can create a new dashboard. */
    canCreateDashboards: boolean;
    /** Whether or not the user can edit the current dashboard. */
    canEditDashboard: boolean;
    /** Whether or not the user can manage permissions for the current dashboard. */
    canManagePermissionsForDashboard: boolean;
}

interface TelemetryState {
    /** Indicates whether we published "Dirty TTI" telemetry already. */
    publishedDirtyTTI: boolean;
}

export interface ErrorState {
    /** The message of an error. */
    errorMessage: string;
    /** Indicates a "refresh the dashboard" prompt with link should be shown when displaying the error. */
    showRefreshLink: boolean;
}

export interface CopyDashboardState {
    /** Dictates if we want to use the dedicated api */
    useDedicatedApi : boolean,

    /** List of projects fetched */
    projects: Project[],

    /** Project team list cached */
    projectTeamList: {[id:string]:TeamProjectItem}
}
